export interface QuoteLanguage {
  code: string;
  label: string;
  /** BCP-47 locale used for text-to-speech; falls back to `code` when unset. */
  speechLocale?: string;
}

// Every language Google Translate supports (codes as its translate endpoint
// expects them, e.g. "zh-CN", "iw", "mni-Mtei"). "en" is first and is the
// default; the rest are alphabetical, as in Google Translate's picker.
export const QUOTE_LANGUAGES: QuoteLanguage[] = [
  { code: "en", label: "English", speechLocale: "en-US" },
  { code: "ab", label: "Abkhaz" },
  { code: "ace", label: "Acehnese" },
  { code: "ach", label: "Acholi" },
  { code: "aa", label: "Afar" },
  { code: "af", label: "Afrikaans" },
  { code: "sq", label: "Albanian" },
  { code: "alz", label: "Alur" },
  { code: "am", label: "Amharic" },
  { code: "ar", label: "Arabic", speechLocale: "ar-SA" },
  { code: "hy", label: "Armenian" },
  { code: "as", label: "Assamese" },
  { code: "av", label: "Avar" },
  { code: "awa", label: "Awadhi" },
  { code: "ay", label: "Aymara" },
  { code: "az", label: "Azerbaijani" },
  { code: "ban", label: "Balinese" },
  { code: "bal", label: "Baluchi" },
  { code: "bm", label: "Bambara" },
  { code: "bci", label: "Baoulé" },
  { code: "ba", label: "Bashkir" },
  { code: "eu", label: "Basque" },
  { code: "btx", label: "Batak Karo" },
  { code: "bts", label: "Batak Simalungun" },
  { code: "bbc", label: "Batak Toba" },
  { code: "be", label: "Belarusian" },
  { code: "bem", label: "Bemba" },
  { code: "bn", label: "Bengali", speechLocale: "bn-IN" },
  { code: "bew", label: "Betawi" },
  { code: "bho", label: "Bhojpuri" },
  { code: "bik", label: "Bikol" },
  { code: "bs", label: "Bosnian" },
  { code: "br", label: "Breton" },
  { code: "bg", label: "Bulgarian" },
  { code: "bua", label: "Buryat" },
  { code: "yue", label: "Cantonese" },
  { code: "ca", label: "Catalan" },
  { code: "ceb", label: "Cebuano" },
  { code: "ch", label: "Chamorro" },
  { code: "ce", label: "Chechen" },
  { code: "ny", label: "Chichewa" },
  { code: "zh-CN", label: "Chinese (Simplified)", speechLocale: "zh-CN" },
  { code: "zh-TW", label: "Chinese (Traditional)", speechLocale: "zh-TW" },
  { code: "chk", label: "Chuukese" },
  { code: "cv", label: "Chuvash" },
  { code: "co", label: "Corsican" },
  { code: "crh", label: "Crimean Tatar (Cyrillic)" },
  { code: "crh-Latn", label: "Crimean Tatar (Latin)" },
  { code: "hr", label: "Croatian" },
  { code: "cs", label: "Czech" },
  { code: "da", label: "Danish" },
  { code: "fa-AF", label: "Dari" },
  { code: "dv", label: "Dhivehi" },
  { code: "din", label: "Dinka" },
  { code: "doi", label: "Dogri" },
  { code: "dov", label: "Dombe" },
  { code: "nl", label: "Dutch" },
  { code: "dyu", label: "Dyula" },
  { code: "dz", label: "Dzongkha" },
  { code: "eo", label: "Esperanto" },
  { code: "et", label: "Estonian" },
  { code: "ee", label: "Ewe" },
  { code: "fo", label: "Faroese" },
  { code: "fj", label: "Fijian" },
  { code: "tl", label: "Filipino" },
  { code: "fi", label: "Finnish" },
  { code: "fon", label: "Fon" },
  { code: "fr", label: "French", speechLocale: "fr-FR" },
  { code: "fr-CA", label: "French (Canada)" },
  { code: "fy", label: "Frisian" },
  { code: "fur", label: "Friulian" },
  { code: "ff", label: "Fulani" },
  { code: "gaa", label: "Ga" },
  { code: "gl", label: "Galician" },
  { code: "ka", label: "Georgian" },
  { code: "de", label: "German", speechLocale: "de-DE" },
  { code: "el", label: "Greek" },
  { code: "gn", label: "Guarani" },
  { code: "gu", label: "Gujarati", speechLocale: "gu-IN" },
  { code: "ht", label: "Haitian Creole" },
  { code: "cnh", label: "Hakha Chin" },
  { code: "ha", label: "Hausa" },
  { code: "haw", label: "Hawaiian" },
  { code: "iw", label: "Hebrew" },
  { code: "hil", label: "Hiligaynon" },
  { code: "hi", label: "Hindi", speechLocale: "hi-IN" },
  { code: "hmn", label: "Hmong" },
  { code: "hu", label: "Hungarian" },
  { code: "hrx", label: "Hunsrik" },
  { code: "iba", label: "Iban" },
  { code: "is", label: "Icelandic" },
  { code: "ig", label: "Igbo" },
  { code: "ilo", label: "Ilocano" },
  { code: "id", label: "Indonesian" },
  { code: "iu-Latn", label: "Inuktut (Latin)" },
  { code: "iu", label: "Inuktut (Syllabics)" },
  { code: "ga", label: "Irish" },
  { code: "it", label: "Italian", speechLocale: "it-IT" },
  { code: "jam", label: "Jamaican Patois" },
  { code: "ja", label: "Japanese", speechLocale: "ja-JP" },
  { code: "jw", label: "Javanese" },
  { code: "kac", label: "Jingpo" },
  { code: "kl", label: "Kalaallisut" },
  { code: "kn", label: "Kannada", speechLocale: "kn-IN" },
  { code: "kr", label: "Kanuri" },
  { code: "pam", label: "Kapampangan" },
  { code: "kk", label: "Kazakh" },
  { code: "kha", label: "Khasi" },
  { code: "km", label: "Khmer" },
  { code: "cgg", label: "Kiga" },
  { code: "kg", label: "Kikongo" },
  { code: "rw", label: "Kinyarwanda" },
  { code: "ktu", label: "Kituba" },
  { code: "trp", label: "Kokborok" },
  { code: "kv", label: "Komi" },
  { code: "gom", label: "Konkani" },
  { code: "ko", label: "Korean", speechLocale: "ko-KR" },
  { code: "kri", label: "Krio" },
  { code: "ku", label: "Kurdish (Kurmanji)" },
  { code: "ckb", label: "Kurdish (Sorani)" },
  { code: "ky", label: "Kyrgyz" },
  { code: "lo", label: "Lao" },
  { code: "ltg", label: "Latgalian" },
  { code: "la", label: "Latin" },
  { code: "lv", label: "Latvian" },
  { code: "lij", label: "Ligurian" },
  { code: "li", label: "Limburgish" },
  { code: "ln", label: "Lingala" },
  { code: "lt", label: "Lithuanian" },
  { code: "lmo", label: "Lombard" },
  { code: "lg", label: "Luganda" },
  { code: "luo", label: "Luo" },
  { code: "lb", label: "Luxembourgish" },
  { code: "mk", label: "Macedonian" },
  { code: "mad", label: "Madurese" },
  { code: "mai", label: "Maithili" },
  { code: "mak", label: "Makassar" },
  { code: "mg", label: "Malagasy" },
  { code: "ms", label: "Malay" },
  { code: "ms-Arab", label: "Malay (Jawi)" },
  { code: "ml", label: "Malayalam", speechLocale: "ml-IN" },
  { code: "mt", label: "Maltese" },
  { code: "mam", label: "Mam" },
  { code: "gv", label: "Manx" },
  { code: "mi", label: "Maori" },
  { code: "mr", label: "Marathi", speechLocale: "mr-IN" },
  { code: "mh", label: "Marshallese" },
  { code: "mwr", label: "Marwadi" },
  { code: "mfe", label: "Mauritian Creole" },
  { code: "chm", label: "Meadow Mari" },
  { code: "mni-Mtei", label: "Meiteilon (Manipuri)" },
  { code: "min", label: "Minang" },
  { code: "lus", label: "Mizo" },
  { code: "mn", label: "Mongolian" },
  { code: "my", label: "Myanmar (Burmese)" },
  { code: "nhe", label: "Nahuatl (Eastern Huasteca)" },
  { code: "ndc-ZW", label: "Ndau" },
  { code: "nr", label: "Ndebele (South)" },
  { code: "new", label: "Nepalbhasa (Newari)" },
  { code: "ne", label: "Nepali" },
  { code: "bm-Nkoo", label: "NKo" },
  { code: "no", label: "Norwegian" },
  { code: "nus", label: "Nuer" },
  { code: "oc", label: "Occitan" },
  { code: "or", label: "Odia (Oriya)" },
  { code: "om", label: "Oromo" },
  { code: "os", label: "Ossetian" },
  { code: "pag", label: "Pangasinan" },
  { code: "pap", label: "Papiamento" },
  { code: "ps", label: "Pashto" },
  { code: "fa", label: "Persian" },
  { code: "pl", label: "Polish" },
  { code: "pt", label: "Portuguese (Brazil)", speechLocale: "pt-BR" },
  { code: "pt-PT", label: "Portuguese (Portugal)", speechLocale: "pt-PT" },
  { code: "pa", label: "Punjabi (Gurmukhi)", speechLocale: "pa-IN" },
  { code: "pa-Arab", label: "Punjabi (Shahmukhi)" },
  { code: "qu", label: "Quechua" },
  { code: "kek", label: "Q'eqchi'" },
  { code: "rom", label: "Romani" },
  { code: "ro", label: "Romanian" },
  { code: "rn", label: "Rundi" },
  { code: "ru", label: "Russian", speechLocale: "ru-RU" },
  { code: "se", label: "Sami (North)" },
  { code: "sm", label: "Samoan" },
  { code: "sg", label: "Sango" },
  { code: "sa", label: "Sanskrit" },
  { code: "sat-Latn", label: "Santali (Latin)" },
  { code: "sat", label: "Santali (Ol Chiki)" },
  { code: "gd", label: "Scots Gaelic" },
  { code: "nso", label: "Sepedi" },
  { code: "sr", label: "Serbian" },
  { code: "st", label: "Sesotho" },
  { code: "crs", label: "Seychellois Creole" },
  { code: "shn", label: "Shan" },
  { code: "sn", label: "Shona" },
  { code: "scn", label: "Sicilian" },
  { code: "szl", label: "Silesian" },
  { code: "sd", label: "Sindhi" },
  { code: "si", label: "Sinhala" },
  { code: "sk", label: "Slovak" },
  { code: "sl", label: "Slovenian" },
  { code: "so", label: "Somali" },
  { code: "es", label: "Spanish", speechLocale: "es-ES" },
  { code: "su", label: "Sundanese" },
  { code: "sus", label: "Susu" },
  { code: "sw", label: "Swahili" },
  { code: "ss", label: "Swati" },
  { code: "sv", label: "Swedish" },
  { code: "ty", label: "Tahitian" },
  { code: "tg", label: "Tajik" },
  { code: "ber-Latn", label: "Tamazight" },
  { code: "ber", label: "Tamazight (Tifinagh)" },
  { code: "ta", label: "Tamil", speechLocale: "ta-IN" },
  { code: "tt", label: "Tatar" },
  { code: "te", label: "Telugu", speechLocale: "te-IN" },
  { code: "tet", label: "Tetum" },
  { code: "th", label: "Thai" },
  { code: "bo", label: "Tibetan" },
  { code: "ti", label: "Tigrinya" },
  { code: "tiv", label: "Tiv" },
  { code: "tpi", label: "Tok Pisin" },
  { code: "to", label: "Tongan" },
  { code: "ts", label: "Tsonga" },
  { code: "tn", label: "Tswana" },
  { code: "tcy", label: "Tulu" },
  { code: "tum", label: "Tumbuka" },
  { code: "tr", label: "Turkish" },
  { code: "tk", label: "Turkmen" },
  { code: "tyv", label: "Tuvan" },
  { code: "ak", label: "Twi" },
  { code: "udm", label: "Udmurt" },
  { code: "uk", label: "Ukrainian" },
  { code: "ur", label: "Urdu", speechLocale: "ur-PK" },
  { code: "ug", label: "Uyghur" },
  { code: "uz", label: "Uzbek" },
  { code: "ve", label: "Venda" },
  { code: "vec", label: "Venetian" },
  { code: "vi", label: "Vietnamese" },
  { code: "war", label: "Waray" },
  { code: "cy", label: "Welsh" },
  { code: "wo", label: "Wolof" },
  { code: "xh", label: "Xhosa" },
  { code: "sah", label: "Yakut" },
  { code: "yi", label: "Yiddish" },
  { code: "yo", label: "Yoruba" },
  { code: "yua", label: "Yucatec Maya" },
  { code: "zap", label: "Zapotec" },
  { code: "zu", label: "Zulu" },
];

export const DEFAULT_QUOTE_LANGUAGE = "en";
